import { z } from 'zod';
import { normalizedUuidSchema } from './common';
import { IMAGE_CONTENT_TYPE_EXTENSIONS } from './image-asset';

export const portfolioAssetPurposeSchema = z.enum(['IMAGE', 'LOGO']);
export type PortfolioAssetPurpose = z.infer<typeof portfolioAssetPurposeSchema>;
export const portfolioTimestampSchema = z.iso.datetime({ precision: 3 });
export const portfolioSha256Schema = z.string().regex(/^[0-9a-f]{64}$/);

const extensionSchema = z.enum([
  IMAGE_CONTENT_TYPE_EXTENSIONS['image/jpeg'],
  IMAGE_CONTENT_TYPE_EXTENSIONS['image/png'],
  IMAGE_CONTENT_TYPE_EXTENSIONS['image/webp'],
]);

/** 서버가 생성하는 정규 자산 키. 입력 키나 URL을 정규화하는 함수가 아니다. */
export function portfolioAssetKeys(assetId: string, purpose: string, extension: string) {
  const id = normalizedUuidSchema.parse(assetId);
  const validPurpose = portfolioAssetPurposeSchema.parse(purpose);
  const ext = extensionSchema.parse(extension);
  const prefix = `portfolio/${validPurpose === 'IMAGE' ? 'images' : 'logos'}/${id}`;
  return {
    originalKey: `${prefix}/original.${ext}`,
    variants:
      validPurpose === 'IMAGE'
        ? [`${prefix}/thumb.webp`, `${prefix}/medium.webp`, `${prefix}/large.webp`]
        : [`${prefix}/display.webp`],
    manifestKey: `${prefix}/complete.json`,
    deletionMarkerKey: `${prefix}/deleting.json`,
  };
}

export function portfolioAssetLockKey(assetId: string): string {
  return `portfolio:asset:${normalizedUuidSchema.parse(assetId)}`;
}

/** All possible current object paths must remain fenced, including originals never uploaded. */
export function portfolioAssetFenceKeys(
  assetId: string,
  purpose: PortfolioAssetPurpose,
): readonly string[] {
  const id = normalizedUuidSchema.parse(assetId);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(id))
    throw new Error('유효한 자산 UUID가 필요합니다.');
  const keys = portfolioAssetKeys(id, purpose, 'jpg');
  return [
    ...['jpg', 'png', 'webp'].map(
      (extension) => portfolioAssetKeys(id, purpose, extension).originalKey,
    ),
    ...keys.variants,
    keys.manifestKey,
  ];
}

/** 완료 표시는 모든 파생 파일이 준비된 묶음만 허용한다. */
export const portfolioAssetManifestSchema = z
  .strictObject({
    assetId: normalizedUuidSchema,
    purpose: portfolioAssetPurposeSchema,
    ownerAdminId: normalizedUuidSchema,
    createdAt: portfolioTimestampSchema,
    completedAt: portfolioTimestampSchema,
    originalKey: z.string(),
    variants: z.array(z.strictObject({ key: z.string(), sha256: portfolioSha256Schema })),
  })
  .superRefine((manifest, ctx) => {
    // 형식 검사에 실패한 문자열에도 refinement가 호출될 수 있다.
    if (!normalizedUuidSchema.safeParse(manifest.assetId).success) return;
    const extension = extensionSchema.safeParse(manifest.originalKey.split('.').at(-1));
    if (!extension.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['originalKey'],
        message: '지원하지 않는 원본 확장자입니다.',
      });
      return;
    }
    const keys = portfolioAssetKeys(manifest.assetId, manifest.purpose, extension.data);
    if (manifest.originalKey !== keys.originalKey) {
      ctx.addIssue({
        code: 'custom',
        path: ['originalKey'],
        message: '자산과 용도에 맞는 원본 키가 필요합니다.',
      });
    }
    const variantKeys = manifest.variants.map(({ key }) => key);
    if (
      variantKeys.length !== keys.variants.length ||
      new Set(variantKeys).size !== variantKeys.length ||
      keys.variants.some((key) => !variantKeys.includes(key))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['variants'],
        message: '자산에 속하는 모든 파생 파일이 필요합니다.',
      });
    }
    if (manifest.completedAt < manifest.createdAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['completedAt'],
        message: '완료 시각은 생성 시각보다 빠를 수 없습니다.',
      });
    }
  });
export type PortfolioAssetManifest = z.infer<typeof portfolioAssetManifestSchema>;

export const portfolioDeletionMarkerSchema = z.strictObject({
  assetId: normalizedUuidSchema,
  markedAt: portfolioTimestampSchema,
  reason: z.string().trim().min(1),
});
export type PortfolioDeletionMarker = z.infer<typeof portfolioDeletionMarkerSchema>;
